import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react"

import { ChevronRight } from "lucide-react"

import { cn } from "@/utils/cn"

interface FilterTriggerProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "disabled" | "onClick" | "onPointerEnter" | "onPointerLeave"
> {
  label: string
  icon: ReactNode
  isActive: boolean
  /**
   * Omit when the trigger is handed to a popover as its `asChild` target — the
   * popover supplies the click that opens it, and a second handler here would
   * toggle the same state twice.
   */
  onClick?: () => void
  disabled?: boolean
  /**
   * Hover handlers from `useFilterSectionHover`, for the sections that anchor
   * their own flyout instead of going through `FilterPopover`.
   */
  onPointerEnter?: (event: React.PointerEvent) => void
  onPointerLeave?: () => void
}

export const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
  (
    {
      label,
      icon,
      isActive,
      onClick,
      disabled = false,
      onPointerEnter,
      onPointerLeave,
      className,
      ...buttonProps
    },
    ref,
  ) => {
    return (
      <button
        {...buttonProps}
        ref={ref}
        type="button"
        onPointerEnter={disabled ? undefined : onPointerEnter}
        onPointerLeave={disabled ? undefined : onPointerLeave}
        className={cn(
          "flex w-full items-center justify-between rounded-md px-3 py-2 text-left",
          "transition-colors",
          disabled && "opacity-60",
          !disabled && "cursor-pointer",
          // An active row keeps its fill on hover; only an inactive one takes the
          // neutral hover.
          !disabled && !isActive && "hover:bg-surface-1",
          // Same fill as a selection chip, so a selected menu row and the chip
          // it produces read as one blue.
          !disabled && isActive && "bg-coval-info-surface",
          className,
        )}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
      >
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "w-4 h-4 flex items-center justify-center",
              isActive && !disabled
                ? "text-coval-info-on-surface"
                : "text-coval-text-muted",
            )}
          >
            {icon}
          </span>
          <span
            className={cn(
              "text-sm font-medium",
              isActive && !disabled
                ? "text-coval-info-on-surface"
                : "text-coval-text",
            )}
          >
            {label}
          </span>
        </div>
        {!disabled && (
          <ChevronRight
            className={cn(
              "w-3 h-3",
              isActive ? "text-coval-info-on-surface" : "text-coval-text-muted",
            )}
          />
        )}
      </button>
    )
  },
)

FilterTrigger.displayName = "FilterTrigger"
